import CoreApi from '../core-api'
import Experiments from '@wix/wix-experiments'
import { ContactSyncProps } from '../../../panels/contact-sync-panel/components/contact-sync-panel'
import { SyncField, SyncData } from '../../../panels/contact-sync-panel/constants/types'
import { getFormPlugins } from '../plugins/utils'
import { isNewCustomFieldKey, areSyncDataEqual } from '../../../panels/contact-sync-panel/utils'
import {
  getSyncedFieldsCount,
  createSyncFieldsFromFormFields,
  mapComplexInnerFieldTypeToServerCrmType,
} from './utils'
import RemoteApi from '../../../panels/commons/remote-api'
import _ from 'lodash'
import { CustomFieldResponse } from '../../../constants/field-types'
import { CUSTOM_FIELD } from '../../../constants/crm-types-tags'
import { getCountableFields } from '../fields/utils'
import { FIELDS } from '../../../constants/roles'
import { FieldDataType } from '@wix/ambassador-contacts-v4-extended-field/types'

export default class ContactSyncApi {
  private biLogger: any
  private boundEditorSDK: BoundEditorSDK
  private coreApi: CoreApi
  private remoteApi: RemoteApi
  private ravenInstance
  private experiments: Experiments

  constructor(
    boundEditorSDK,
    coreApi: CoreApi,
    remoteApi,
    { biLogger, ravenInstance, experiments },
  ) {
    this.boundEditorSDK = boundEditorSDK
    this.coreApi = coreApi
    this.biLogger = biLogger
    this.remoteApi = remoteApi
    this.ravenInstance = ravenInstance
    this.experiments = experiments
  }

  public async loadInitialPanelData({
    formComponentRef,
    componentConnection,
  }: {
    formComponentRef: ComponentRef
    componentConnection: ComponentConnection
  }): Promise<Partial<ContactSyncProps>> {
    const plugins = getFormPlugins(componentConnection)

    return Promise.all([
      this.coreApi.fields.getFieldsSortByXY(formComponentRef),
      this.remoteApi.getCustomFields(),
    ]).then(([fieldsOnStage, customFields]) => ({
      loading: false,
      fields: createSyncFieldsFromFormFields(
        getCountableFields(fieldsOnStage),
        plugins,
        customFields,
      ),
      customFields,
      preset: _.get(componentConnection, 'config.preset'),
    }))
  }

  private async _createCustomField(customFieldName: string, customFieldType: FieldDataType) {
    return this.remoteApi.createCustomField({
      name: customFieldName,
      fieldType: customFieldType,
    })
  }

  private _saveCustomField(customField: CustomFieldResponse): Promise<void> {
    return this.remoteApi.updateCustomFieldName(customField.key, customField.name)
  }

  public async createCustomField(
    fieldComponentRef: ComponentRef,
    customFieldName: string,
    customFieldType: FieldDataType,
  ): Promise<SyncData> {
    const customField = await this._createCustomField(customFieldName, customFieldType)
    const updatedSync = {
      crmType: CUSTOM_FIELD,
      crmTag: undefined,
      customFieldKey: customField.key,
      customFieldName,
    }
    await this.saveCurrentSyncData(fieldComponentRef, updatedSync)
    return updatedSync
  }

  public async saveCustomField(
    fieldComponentRef: ComponentRef,
    customFieldToUpdate: CustomFieldResponse,
  ): Promise<SyncData> {
    await this._saveCustomField(customFieldToUpdate)
    const updatedSync = {
      crmType: CUSTOM_FIELD,
      crmTag: undefined,
      customFieldKey: customFieldToUpdate.key,
      customFieldName: customFieldToUpdate.name,
    }
    await this.saveCurrentSyncData(fieldComponentRef, updatedSync)
    return updatedSync
  }

  private _shouldSaveInnerFieldsSyncData(field: FormField) {
    return field.role === FIELDS.ROLE_FIELD_COMPLEX_ADDRESS_WIDGET
  }

  private async _saveComplexInnerFieldsSyncData(field: FormField, data: SyncData) {
    const { childFields } = field

    return Promise.all(
      childFields.map((childField) => {
        const syncData = {
          crmTag: data.crmTag,
          customFieldKey: childField.customFieldKey,
          customFieldName: childField.customFieldName,
          crmType:
            data.crmType === 'customField'
              ? 'customField'
              : mapComplexInnerFieldTypeToServerCrmType.complexAddress[childField.fieldType],
        }

        return this.coreApi.setComponentConnectionsByConnectionRole(
          childField.componentRef,
          syncData,
          childField.role,
        )
      }),
    )
  }

  public async saveCurrentSyncData(fieldRef: ComponentRef, data: SyncData) {
    const field: FormField = await this.coreApi.fields.getField(fieldRef)
    const shouldSaveInnerFieldsSyncData = this._shouldSaveInnerFieldsSyncData(field)

    if (shouldSaveInnerFieldsSyncData) {
      await this._saveComplexInnerFieldsSyncData(field, data)
    }

    return this.coreApi.setComponentConnection(fieldRef, data, false)
  }

  public async saveContactSyncFieldsAndReturnSyncedFieldsCount(
    fields: SyncField[],
    initialFields: SyncField[],
    customFields: CustomFieldResponse[],
    initialCustomFields: CustomFieldResponse[],
  ): Promise<{
    syncedFieldsCount: number
  }> {
    const initialCustomFieldsByKey = _.keyBy(initialCustomFields, 'key')

    const initialFieldsById = _.keyBy(initialFields, 'componentRef.id')

    const changedFields = fields.filter(
      (field) =>
        !areSyncDataEqual(field.syncData, initialFieldsById[field.componentRef.id].syncData),
    )
    const fieldsWithNewCustomField = changedFields.filter((field) =>
      isNewCustomFieldKey(field.syncData?.customFieldKey),
    )
    const fieldsWithChangedSync = changedFields.filter(
      (field) => !isNewCustomFieldKey(field.syncData.customFieldKey),
    )

    const customFieldsToUpdate = customFields
      .filter((customField) => !isNewCustomFieldKey(customField.key))
      .filter(
        ({ name, key }) =>
          initialCustomFieldsByKey[key].name !== name &&
          _.some(fieldsWithChangedSync, (field) => field.syncData.customFieldKey === key),
      )

    const fieldsWithCreatedNewCustomField = await Promise.all(
      fieldsWithNewCustomField.map<Promise<{ componentRef: ComponentRef; syncData: SyncData }>>(
        async (field) => {
          const customField = await this._createCustomField(
            field.syncData.customFieldName,
            field.customFields[0],
          )
          return {
            componentRef: field.componentRef,
            syncData: {
              ...field.syncData,
              customFieldKey: customField.key,
            },
          }
        },
      ),
    )

    await Promise.all(customFieldsToUpdate.map(this._saveCustomField.bind(this)))

    const fieldsToUpdateConfig = _.flatten([fieldsWithCreatedNewCustomField, fieldsWithChangedSync])

    await Promise.all(
      fieldsToUpdateConfig.map((field) =>
        this.saveCurrentSyncData(field.componentRef, field.syncData),
      ),
    )

    return {
      syncedFieldsCount: getSyncedFieldsCount(
        fields.map(({ syncData: { crmType, customFieldKey } }) => ({
          crmType,
          customFieldKey,
        })),
      ),
    }
  }
}
